import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { isAdminRole } from '../../utils/roleHelpers';

const HealthStatusWidget = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    if (isAdminRole(user?.role)) {
      fetchHealthStatus();
      // Refresh every 5 minutes
      const interval = setInterval(fetchHealthStatus, 300000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchHealthStatus = async () => {
    setLoading(true);
    
    try {
      // Get basic performance stats
      const response = await axios.get('/api/performance/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Calculate health status
      const data = response.data;
      const totalCollections = data.database?.collections || 0;
      const totalIndexes = Object.values(data.collections || {})
        .reduce((sum, collection) => sum + (collection.indexCount || 0), 0);
      
      const indexRatio = totalCollections > 0 ? totalIndexes / totalCollections : 0;
      const dataSize = data.database?.dataSize || 0;
      const indexSize = data.database?.indexSize || 0;
      
      setHealthData({
        status: indexRatio > 3 ? 'healthy' : indexRatio > 1.5 ? 'warning' : 'critical',
        collections: totalCollections,
        indexes: totalIndexes,
        indexRatio: indexRatio.toFixed(1),
        dataSize: formatBytes(dataSize),
        indexSize: formatBytes(indexSize),
        timestamp: new Date()
      });
    } catch (err) {
      console.error('Health status fetch error:', err);
      setHealthData({
        status: 'error',
        error: 'Failed to fetch health data',
        timestamp: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'error':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'critical':
        return '🚨';
      case 'error':
        return '❌';
      default:
        return '⚪';
    }
  };

  if (!isAdminRole(user?.role)) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-900">Database Health</h3>
        <button
          onClick={fetchHealthStatus}
          disabled={loading}
          className="text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50"
        >
          {loading ? '🔄' : '🔄 Refresh'}
        </button>
      </div>

      {healthData ? (
        <div className="space-y-3">
          {/* Status Badge */}
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(healthData.status)}`}>
            <span className="mr-2">{getStatusIcon(healthData.status)}</span>
            {healthData.status === 'error' ? 'Connection Error' : 
             healthData.status === 'healthy' ? 'Healthy' :
             healthData.status === 'warning' ? 'Needs Attention' : 'Critical'}
          </div>

          {/* Health Details */}
          {healthData.status !== 'error' ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Collections</div>
                <div className="font-semibold">{healthData.collections}</div>
              </div>
              <div>
                <div className="text-gray-500">Total Indexes</div>
                <div className="font-semibold">{healthData.indexes}</div>
              </div>
              <div>
                <div className="text-gray-500">Data Size</div>
                <div className="font-semibold">{healthData.dataSize}</div>
              </div>
              <div>
                <div className="text-gray-500">Index Coverage</div>
                <div className="font-semibold">{healthData.indexRatio}x</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-red-600">
              {healthData.error}
            </div>
          )}

          {/* Last Updated */}
          <div className="text-xs text-gray-400">
            Updated: {healthData.timestamp.toLocaleTimeString()}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-sm text-gray-600">Loading...</span>
        </div>
      )}
    </div>
  );
};

export default HealthStatusWidget;
